import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { X } from 'lucide-react';
import debounce from 'lodash.debounce';
import api from 'config/axiosConfig';
import { Influence } from 'util/constants';
import InputField from 'ui/InputField';
import EntityAvatar from 'components/Entities/EntityAvatar';

interface Props {
  onSelect: (suggestion: Influence) => void;
  onEntityChipClose?: () => void;
  onSearchChange?: () => void;
  placeholder?: string;
  excludeInfluenceIds?: number[];
  inputType?: 'search' | 'add';
  className?: string;
  autoFocus?: boolean;
  fieldRequired?: boolean;
  validationMessage?: string;
  validationState?: 'none' | 'valid' | 'invalid';
  fieldValue?: string;
  prepopulateData?: Influence | null;
}

const InfluenceSearch: React.FC<Props> = ({ 
  onSelect, 
  onSearchChange,
  placeholder = "Search influences...",
  excludeInfluenceIds = [],
  inputType = 'search',
  className = "",
  autoFocus = false,
  fieldRequired = false,
  fieldValue = '',
  validationMessage = '',
  validationState = 'none',
  onEntityChipClose,
  prepopulateData = null
}) => {
  const [searchQuery, setSearchQuery] = useState(fieldValue || '');
  const [searchSuggestions, setSearchSuggestions] = useState<Influence[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [influenceSelected, setInfluenceSelected] = useState(false);
  const [selectedInfluence, setSelectedInfluence] = useState<Influence | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN;
  
  useLayoutEffect(() => {
    if (prepopulateData) {
      setSearchQuery(prepopulateData.influencer_name);
      setInfluenceSelected(true);
      setSelectedInfluence(prepopulateData);
    } else {
      setInfluenceSelected(false);
      setSelectedInfluence(null);
      setSearchQuery('');
    }
  }, [prepopulateData]);

  useEffect(() => {
    if (showSuggestions) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [showSuggestions]);

  const performSearch = async (value: string) => {
    if (!value) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await api.get(`/api/entities?entityType=influence&searchQuery=${encodeURIComponent(value)}&excludeInfluenceIds=[${excludeInfluenceIds.join(',')}]`);
      const influenceResults: Influence[] = response.data.filter((influence: Influence) => 
        !excludeInfluenceIds.some((excludeId) => excludeId === influence.id)
      );
      const filteredInfluenceResults: Influence[] = influenceResults.map((influence) => (influence as any).attributes);

      setSearchSuggestions(filteredInfluenceResults);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching search results:', error);
    }
  };

  //eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => performSearch(value), 200),
    []
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchQuery(value);
    setShowSuggestions(true);
    debouncedSearch(value);
    if (onSearchChange) {
      onSearchChange();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
    searchInputRef.current?.blur();
  };

  const handleSelectInfluence = (influence: Influence) => {
    onSelect(influence);
    setSelectedInfluence(influence);
    setShowSuggestions(false);
    setSearchQuery(influence.influencer_name);
    setInfluenceSelected(true);
  };

  const handleEntityChipClose = () => {
    setSelectedInfluence(null);
    setInfluenceSelected(false);
    setSearchQuery('');
    if (onEntityChipClose) {
      onEntityChipClose();
    }
    document.getElementById(`influence-add-${placeholder}`)?.focus();
  };

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
      {inputType === 'search' ? (
        <>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            ref={searchInputRef}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="eoe-input w-full bg-gray-200 dark:bg-eoe-dark-input rounded-full px-2 py-2 pr-8"
          />
          {searchQuery.length > 0 && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100 p-1"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </>
        ) : (
        <>
          {influenceSelected ? (
            <span className={`input-container relative flex-1`}>
                <div
                  className={`
                      w-full min-h-[60px] px-3 pt-6 pb-1
                      eoe-input bg-gray-200 dark:bg-eoe-dark-input
                      rounded-2xl
                      text-gray-800 dark:text-eoe-dark-text
                      transition-colors
                      resize-none
                  `}
                >
                <span 
                    className={`
                        absolute left-3
                        top-2 text-xs
                        transition-all
                        text-eoe-text-meta dark:text-eoe-dark-text-meta
                    `}
                >
                    {placeholder}
                    {fieldRequired && <span className="text-warning-red dark:text-dark-warning-red ml-[0.5px]">*</span>}
                </span>
                <EntityAvatar
                  entity={selectedInfluence as Influence}
                  entityType="influence"
                  entityChip={true}
                  onEntityChipClose={handleEntityChipClose}
                />
                </div>
              </span>
          ) : (
            <InputField
              id={`influence-add-${placeholder}`}
              type="text"
              name={`influence-add-${placeholder}`}
              labelText={placeholder}
              fieldRequired={fieldRequired}
              value={searchQuery || fieldValue}
              onChange={handleSearchChange}
              autoComplete="off"
              ref={searchInputRef}
              autoFocus={prepopulateData ? true : autoFocus}
              validationState={validationState}
              error={validationMessage}
            />
          )}
        </>
      )}
      </div>

      {showSuggestions && searchSuggestions.length > 0 && (
      <div
        className={`search-suggestions-container flex flex-col bg-transparent 
          ${inputType === 'search' ?
          'fixed top-[56px] left-[1px] right-[1px] z-50 max-h-[calc(100dvh-72px)] overflow-hidden md:w-[580px] md:mx-auto md:left-0 md:right-0' : 
          `relative bottom-2 top-1 max-h-[200px] overflow-y-auto`}
        `}
      >
        <ul
          className={`bg-eoe-bg dark:bg-eoe-dark-bg border border-eoe-border-main dark:border-eoe-dark-border-main border-b-0 shadow-lg
            ${inputType === 'search' ? 
              'flex-shrink overflow-y-auto max-h-[calc(100dvh-72px)]' :
              'flex-1 overflow-y-auto'
            }
          `}
        >
          {searchSuggestions.map((influence, index) => (
            <li
              key={index}
              className="
                group
                px-3 py-2
                hover:bg-eoe-icon dark:hover:bg-eoe-dark-icon
                cursor-pointer
                text-eoe-text dark:text-eoe-dark-text
                flex justify-between items-center
              "
              onClick={() => handleSelectInfluence(influence)}
            >
              <div className="flex items-center space-x-2">
                <span className="w-8 h-8">
                  <img 
                    src={influence.influencer_properties?.reference_link_img_key ? 
                      `${IMAGE_CDN}/${influence.influencer_properties.reference_link_img_key}` : 
                      influence.influencer_properties?.subject_properties?.thumbnail?.source || 
                      'https://upload.wikimedia.org/wikipedia/en/8/80/Wikipedia-logo-v2.svg'
                    } 
                    alt="Influence avatar" 
                    className="w-full h-full rounded-full object-cover" 
                  />
                </span>
                <div className="flex flex-col">
                  <span className="flex flex-row items-center gap-x-1 text-sm">
                    {influence.influencer_name}
                    <span className="flex rounded-full px-1 text-xxs bg-eoe-entity-issue text-white flex-shrink-0 items-center justify-center">Influencer</span>
                  </span>
                  <span className="text-xs flex flex-row items-center gap-x-1">
                    {influence.influenced_name}
                    <span className="flex rounded-full px-1 text-xxs bg-eoe-entity-issue text-white flex-shrink-0 items-center justify-center">Influenced</span>
                  </span>
                </div>
              </div>
              <span className="flex rounded-full px-2 text-xs bg-eoe-entity-influence text-white flex-shrink-0 items-center justify-center">Influence</span>
            </li>
          ))}
        </ul>
        <div className="flex-shrink-0 border-none"> 
          <button className="w-full btn-secondary py-[5px] px-2 rounded-tr-none rounded-tl-none" onClick={() => setShowSuggestions(false)}>
            <span className="text-eoe-dark-text">Close search</span>
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default InfluenceSearch;